import * as React from 'react';
import cn from 'classnames';
import { useTheme } from '../../theme';
import { default as BaseButton } from '../../BaseComponents/Button';
import { ButtonVariant } from '../Button/types';
import { CdnIcon, CdnIconNames } from '@shared/ui';

const statusIconKeyMap: Partial<Record<Props['status'], CdnIconNames>> = {
	done: 'Info/Item Icluded',
	error: 'Info/Not included'
};

export interface Props {
	className?: string;
	iconClassName?: string;
	contentClassName?: string;
	titleClassName?: string;
	textClassName?: string;
	contextClassName?: string;
	globalClassName?: string;
	htmlClassName?: string;
	title?: React.ReactNode;
	text?: React.ReactNode;
	image?: string;
	code?: string;
	asHtml?: boolean;
	icon?: React.ReactNode;
	type: 'global' | 'context';
	status: 'note' | 'done' | 'warning' | 'error' | 'subscription';
	actionClassName?: string;
	actionText?: string;
	actionVariant?: ButtonVariant;
	actionLoading?: boolean;
	onClickAction?: () => void;
	renderMessagesWithoutWrapper?: boolean;
	hideIcon?: boolean;
	style?: React.CSSProperties;
}

const renderNode = (node: React.ReactNode, className?: string) =>
	typeof node === 'string' ? (
		<div className={className} dangerouslySetInnerHTML={{ __html: node }} />
	) : (
		<div className={className}>{node}</div>
	);

const Notification: React.FC<Props> = ({
	className,
	iconClassName,
	contentClassName,
	titleClassName,
	textClassName,
	contextClassName,
	globalClassName,
	htmlClassName,
	title,
	text,
	image,
	icon,
	type,
	actionClassName,
	actionText,
	status,
	actionVariant = 'optionAction',
	actionLoading,
	onClickAction,
	code,
	renderMessagesWithoutWrapper,
	asHtml,
	hideIcon,
	style
}) => {
	const { Notification: css } = useTheme('BaseComponents');
	const isContextType = type === 'context';

	if (renderMessagesWithoutWrapper && typeof text === 'string') {
		return <div className={htmlClassName} style={style} dangerouslySetInnerHTML={{ __html: text }} />;
	}

	return (
		<div
			className={cn(css.wrapper, className, {
				[css.context]: !asHtml && isContextType,
				[css.global]: !asHtml && !isContextType,
				[css.success]: status === 'done' && !asHtml,
				[css.subscription]: status === 'subscription',
				[css.warning]: status === 'warning',
				[css.error]: status === 'error',
				[contextClassName]: isContextType && contextClassName,
				[globalClassName]: !isContextType && globalClassName
			})}
			data-code={code}
			style={style}
		>
			{!hideIcon &&
				(image ? (
					<img className={css.image} src={image} alt="" />
				) : (
					<div className={cn(css.icon, iconClassName)}>
						{icon ? icon : <CdnIcon icon={statusIconKeyMap[status] ?? 'Info/Details'} />}
					</div>
				))}

			<div className={css.content__wrapper}>
				<div className={css.wrapper__inner}>
					{asHtml && typeof text === 'string' ? (
						<div className={htmlClassName} dangerouslySetInnerHTML={{ __html: text }} />
					) : (
						<div className={cn(css.content, contentClassName)}>
							{title && renderNode(title, cn(css.title, titleClassName))}
							{text && renderNode(text, cn(css.text, textClassName))}
						</div>
					)}
				</div>

				{actionText && !asHtml && (
					<BaseButton
						variant={actionVariant}
						className={cn(css.action, actionClassName)}
						onClick={onClickAction}
						isLoading={actionLoading}
					>
						{actionText}
					</BaseButton>
				)}
			</div>
		</div>
	);
};

export default Notification;
